import {
	ATTRIBUTES,
	publish,
	PUB_SUB_EVENTS,
	SELECTORS,
    createATCitems
} from '@/scripts/core/global'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import {
	addItemsToCart,
	getDOMCartSectionApiIds,
	renderResponseToCartDrawer
} from '@/scripts/core/cart-functions'

export class ProductPersonalization extends UcoastEl {
    static htmlSelector = 'product-personalization'
    editButtons: NodeListOf<HTMLButtonElement>
    colorInputs: NodeListOf<HTMLInputElement>
    characterInputs: NodeListOf<HTMLInputElement>
    items: NodeListOf<HTMLElement>
    iconButtons: NodeListOf<HTMLButtonElement>
    iconsPopups: NodeListOf<HTMLElement>
    closeButton: HTMLButtonElement
    editItems: NodeListOf<HTMLElement>
    nextButton: HTMLButtonElement
    previousButton: HTMLButtonElement
    form: HTMLFormElement
    submitButton: HTMLButtonElement
    error?: boolean

    addNewItem?: HTMLElement
    addRightButton?: HTMLButtonElement
    leftBackButton?: HTMLButtonElement

    constructor() {
        super()
        this.editButtons = q.rl('.edit-button', this)
        this.colorInputs = q.rl('.product-personalization__color-input', this)
        this.characterInputs = q.rl('.character-input', this)
        this.nextButton = q.rs('.button-finish', this)
        this.previousButton = q.rs('.button-back', this)
        
        this.items = q.rl('.product-personalization__item', this)
        this.iconButtons = q.rl('.product-personalization__icon', this)
        this.iconsPopups = q.rl('.product-personalization__icons', this)
        this.closeButton = q.rs('.product-personalization__close', this)
        this.editItems = q.rl('.product-personalization__info-edit', this)
        this.form = q.rs('form', this)
        this.submitButton = q.rs('.product-personalization__submit', this)

        this.editButtons.forEach(editButton => {
            editButton.addEventListener('click', this.onClickEditButton.bind(this))
        })

        this.iconButtons.forEach(iconButton => {
            iconButton.addEventListener('click', this.onClickIconButton.bind(this))
        })

        this.colorInputs.forEach(colorInput => {
            colorInput.addEventListener('change', this.onChangeColor.bind(this))
        })

        this.characterInputs.forEach(characterInput => {
            characterInput.addEventListener('input', this.onChangeCharacter.bind(this))
            characterInput.addEventListener('focus', (e: Event) => {
                e.preventDefault()
                const inputField = q.rClosestTarget(e, '.product-personalization__input-field')
                const currentWrapper = q.rClosestTarget(e, '.product-personalization__input-content')
                if(!inputField || !currentWrapper) return
                const inputWrappers = q.rl('.product-personalization__input-content', inputField)
                inputWrappers.forEach(inputWrapper => {
                    inputWrapper.classList.toggle('active', inputWrapper == currentWrapper)
                })
            })
            characterInput.addEventListener('keyup', this.onKeyupCharater.bind(this))
        })

        this.nextButton.addEventListener('click', this.next.bind(this))
        this.previousButton.addEventListener('click', this.previous.bind(this))

        this.closeButton.addEventListener('click', this.close.bind(this))

        document.body.addEventListener('click', this.onClickBody.bind(this))

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this))

        if(this.querySelector('.none-added-title')) {
            this.addNewItem = q.rs('.none-added-title', this)
            this.addRightButton = q.rs('.button-add-right', this)
            this.leftBackButton = q.rs('.button-back-left', this)

            this.addNewItem.addEventListener('click', this.onClickEditButton.bind(this))
            this.addRightButton.addEventListener('click', this.toggleItem.bind(this))
            this.leftBackButton.addEventListener('click', this.toggleItem.bind(this))
        }
        this.applyInitialColors()
    }

	// Create a colored mask element for an icon URL
	private createIconMaskElement(url: string, colorHex: string): HTMLSpanElement {
		const span = document.createElement('span')
		span.setAttribute('data-pp-icon-mask', 'true')
		span.setAttribute('aria-hidden', 'true')
		span.style.backgroundColor = colorHex
		;(span.style as any).webkitMaskImage = `url(${url})`
		;(span.style as any).maskImage = `url(${url})`
		return span
	}

	focusNextInput(currentContent: HTMLElement) {
		const inputWrapper = currentContent.closest('.product-personalization__input-wrapper') as HTMLElement | null
		if (!inputWrapper) return
		const contents = q.rl('.product-personalization__input-content', inputWrapper)
		const index = Array.from(contents).indexOf(currentContent)
		const next = contents[index + 1] as HTMLElement | undefined
		if (!next) return
		contents.forEach(c => {
			c.classList.toggle('active', c === next)
		})
		const nextInput = q.os<HTMLInputElement>('input', next)
		if (nextInput) {
			nextInput.focus()
			nextInput.select()
		}
	}

    onChangePersonalizationValue() {
        q.rl('.product-personalization__item', this).forEach(wrapper => {
            const personalizationValues: string[] = []
            q.rl('.product-personalization__input-content', wrapper).forEach(inputContent => {
                const input = q.rs('input', inputContent) as HTMLInputElement
                if(inputContent.dataset.icon) personalizationValues.push(inputContent.dataset.icon as string)
                else if(input.value) personalizationValues.push(input.value)
            })
            const colorInput = q.rs('.product-personalization__color-input:checked', wrapper) as HTMLInputElement
            personalizationValues.push(colorInput.value)
            const selectedValueEl = q.rs('.selected-value', wrapper)
            selectedValueEl.textContent = personalizationValues.join(', ')
        })
    }

    getSelectedColorHex(item: HTMLElement): string | null {
        const input = item.querySelector('.product-personalization__color-input:checked') as HTMLInputElement | null
        return input?.dataset.hex || null
    }

    updateInputTextColors(item: HTMLElement) {
        const monogramHex = this.getSelectedColorHex(item)
        if (!monogramHex) return
        const productHex = this.dataset.productColor || ''
        q.rl('.product-personalization__input-content', item).forEach(wrapper => {
            if (productHex) (wrapper as HTMLElement).style.backgroundColor = productHex
            const input = q.os<HTMLInputElement>('input', wrapper)
            if (input) {
                input.style.backgroundColor = 'transparent'
                input.style.color = monogramHex
            }
			const mask = (wrapper as HTMLElement).querySelector('[data-pp-icon-mask]') as HTMLElement | null
			if (mask) {
				mask.style.backgroundColor = monogramHex
			}
        })
    }

    updateMediaBackgroundFromActive() {
        const activeItem = this.querySelector('.product-personalization__item.active') as HTMLElement | null
        const media = this.querySelector('.product-personalization__media-image') as HTMLElement | null
        if (!activeItem || !media) return
        const hex = this.getSelectedColorHex(activeItem)
        if (!hex) return
        media.style.backgroundColor = hex
    }

    applyInitialColors() {
        const leftItem = this.querySelector('.product-personalization__item[data-option="left"]') as HTMLElement | null
        const rightItem = this.querySelector('.product-personalization__item[data-option="right"]') as HTMLElement | null
        if (leftItem) this.updateInputTextColors(leftItem)
        if (rightItem) this.updateInputTextColors(rightItem)
        this.updateMediaBackgroundFromActive()
    }

    onChangeCharacter(e: Event) {
        e.preventDefault()
        const wrapper = q.rClosestTarget(e, '.product-personalization__input-content')
        wrapper.dataset.icon = ''
		const existingImg = wrapper.querySelector('img')
		if (existingImg) existingImg.remove()
		const existingMask = wrapper.querySelector('[data-pp-icon-mask]')
		if (existingMask) existingMask.remove()
        this.onChangePersonalizationValue()
		const target = e.currentTarget as HTMLInputElement
		if (target && target.value && target.value.length >= (target.maxLength || 1)) {
			this.focusNextInput(wrapper as HTMLElement)
		}
    }

    onKeyupCharater(e: KeyboardEvent) {
        const target = e.currentTarget as HTMLInputElement
        const wrapper = q.rClosestTarget(e, '.product-personalization__input-content')
        if(!wrapper || !target) return
        if(wrapper.dataset.icon && e.code?.toUpperCase() !== 'ESCAPE') {
			const existingImg = wrapper.querySelector('img')
			if (existingImg) existingImg.remove()
			const existingMask = wrapper.querySelector('[data-pp-icon-mask]')
			if (existingMask) existingMask.remove()
            target.value = ''
            wrapper.dataset.icon = ''
            target.dispatchEvent(new Event('input', { bubbles: true }))
        }
        this.onChangePersonalizationValue()
    }

    toggleItem(e: Event) {
        const target = e.currentTarget as HTMLButtonElement
        if(!target) return
        const addRightButton = q.rs('.button-add-right', this)
        const leftBackButton = q.rs('.button-back-left', this)
        if(target.classList.contains('button-add-right')) {
            this.items[0].classList.remove('active')
            this.items[1].classList.add('active')
            addRightButton.classList.add('hidden')
            leftBackButton.classList.remove('hidden')
        } else {
            this.items[0].classList.add('active')
            this.items[1].classList.remove('active')
            addRightButton.classList.remove('hidden')
            leftBackButton.classList.add('hidden')
        }
        this.updateMediaBackgroundFromActive()
    }

    onClickEditButton(e: Event) {
        const target = e.currentTarget as HTMLButtonElement
        if(!target) return
        const wrapper = q.rc(target, '.product-personalization__item')
        this.items.forEach(item => {
            item.classList.toggle('active', wrapper === item)
        })
        this.updateMediaBackgroundFromActive()
    }

    onClickIconButton(e: Event) {
        const target = e.currentTarget as HTMLButtonElement
        if(!target) return
        const img = q.rs('.media img', target) as HTMLImageElement
        const iconHandle = target.dataset.handle
        const wrapper = q.rc(target, '.product-personalization__input-field')
        if(!img || !iconHandle || !wrapper) return
		const inputWrappers = q.rl('.product-personalization__input-content', wrapper)
		let activeContent: HTMLElement | undefined
		inputWrappers.forEach(inputWrapper => {
			if(inputWrapper.classList.contains('active')) {
				const input = q.rs('input', inputWrapper) as HTMLInputElement
				input.value = ''
				inputWrapper.dataset.icon = iconHandle
				const existingImg = inputWrapper.querySelector('img')
				if (existingImg) existingImg.remove()
				const existingMask = inputWrapper.querySelector('[data-pp-icon-mask]')
				if (existingMask) existingMask.remove()
				const item = q.rc(target, '.product-personalization__item') as HTMLElement | null
				const monogramHex = item ? this.getSelectedColorHex(item) : null
				const url = img.src
				const mask = this.createIconMaskElement(url, monogramHex || '#000')
				;(inputWrapper as HTMLElement).dataset.iconUrl = url
				inputWrapper.appendChild(mask)
				activeContent = inputWrapper as HTMLElement
			}
		})
		if (activeContent) this.focusNextInput(activeContent)
        this.onChangePersonalizationValue()
    }

    onClickBody(e: Event) {
        const target = e.target as HTMLElement
        if(!target) return
        if(!target.closest('.add-icon')) {
            this.iconsPopups.forEach(popup => {
                popup.classList.remove('active')
            })
        }
        if(!target.closest('.product-personalization-content') && !target.closest('.trigger-personalization-popup') && !target.closest('cart-edit-button')) {
            this.classList.remove('active')
        }
    }

    close(e: Event) {
        e.preventDefault()
        e.stopPropagation()
        this.classList.remove('active')
    }

    formatCurrency(price: number) {
        const currency = this.submitButton.dataset.currency || 'USD'
        const local = this.submitButton.dataset.local || 'en-US'
        return new Intl.NumberFormat(local, { style: "currency", currency: currency }).format(price)
    }

    onChangeColor(e: Event) {
        const target = e.currentTarget as HTMLInputElement
        if(!target) return
        const item = target.closest('.product-personalization__item') as HTMLElement | null
        const selectedColorEl = item?.querySelector('.product-personalization__selected-color')
        const color = target.value
        if(selectedColorEl && color) selectedColorEl.textContent = color.trim()
        if (item) {
            this.updateInputTextColors(item)
            if (item.classList.contains('active')) this.updateMediaBackgroundFromActive()
            // If preview step is active, update preview colors to reflect new selection
            if (this.editItems[1] && this.editItems[1].classList.contains('active')) {
                const preview = q.os<HTMLElement>('.product-personalization__note-preview', this)
                if (preview) {
                    const productHex = this.dataset.productColor || ''
                    if (productHex) preview.style.backgroundColor = productHex
                    const monogramHex = this.getSelectedColorHex(item)
                    if (monogramHex) preview.style.color = monogramHex
                }
            }
        }
        this.onChangePersonalizationValue()
    }

    next(e: Event) {
        e.preventDefault()
        this.editItems[0].classList.remove('active')
        this.editItems[1].classList.add('active')
        const price = this.formatCurrency((Number(this.dataset.price) +  Number(this.dataset.variantPrice)) / 100)
        const priceEl = q.rs('.product-personalization__submit-price', this.submitButton)
        priceEl.textContent = price
        const personalizationPreview = q.rs('.product-personalization__note-preview', this)
        const inputWrapper = q.rs('.product-personalization__input-wrapper', this)
        if(!personalizationPreview || !inputWrapper) return
        // Apply product color as background and selected monogram color as text color
        const productHex = this.dataset.productColor || ''
        if (productHex) (personalizationPreview as HTMLElement).style.backgroundColor = productHex
        const activeItem = this.querySelector('.product-personalization__item.active') as HTMLElement | null
        const monogramHex = activeItem ? this.getSelectedColorHex(activeItem) : null
        if (monogramHex) (personalizationPreview as HTMLElement).style.color = monogramHex
        personalizationPreview.innerHTML = ''
        q.rl('.product-personalization__input-content', inputWrapper).forEach(content => {
            const input = q.rs('.character-input', content) as HTMLInputElement
            if(!input) return
            if(content.dataset.icon) {
				const url = (content as HTMLElement).dataset.iconUrl || ''
				if (url) {
					const mask = this.createIconMaskElement(url, monogramHex || '#000')
					personalizationPreview.appendChild(mask)
				}
            } else if (input.value) {
                const span = document.createElement('span')
				span.classList.add('h1')
				span.style.color = monogramHex || ''
                span.textContent = input.value
                personalizationPreview.appendChild(span)
            }
        })        
    }

    previous(e: Event) {
        e.preventDefault()
        this.editItems[0].classList.add('active')
        this.editItems[1].classList.remove('active')
    }

    getSpinner() {
		return q.rs(SELECTORS.loadingOverlaySpinner, this.submitButton)
	}

    reset() {
        // Toggle edit sections
        this.editItems?.[0]?.classList.add('active')
        this.editItems?.[1]?.classList.remove('active')

        // Clear input values
        this.characterInputs?.forEach(input => {
            const wrapper = input.closest('.product-personalization__input-content') as HTMLElement
            wrapper.classList.remove('active')
            wrapper.dataset.icon = ''
            if(wrapper.querySelector('img')) wrapper.querySelector('img')?.remove()
            input.value = ''
        })

        // Reset color selections
        const leftColor = this.querySelector('[name="left-color"]') as HTMLInputElement | null
        const rightColor = this.querySelector('[name="right-color"]') as HTMLInputElement | null
        if (leftColor) leftColor.checked = true
        if (rightColor) rightColor.checked = true

        // Reset selected values
        this.querySelectorAll('.selected-value').forEach(el => el.textContent = '')

        // Toggle product items
        const items = this.querySelectorAll('.product-personalization__item')
        items[0]?.classList.add('active')
        items[1]?.classList.remove('active')

        // Mark as added
        const AddedItem = this.querySelector('.product-personalization__item[data-added="true"]')
        if (AddedItem) AddedItem.setAttribute('data-added', 'false')

        if (this.addRightButton) this.addRightButton.classList.remove('hidden')
        if (this.leftBackButton) this.leftBackButton.classList.add('hidden')
        this.applyInitialColors()
    }

    onSubmitHandler(e: Event) {
        e.preventDefault()
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return
        
        this.submitButton.setAttribute('aria-disabled', 'true')
        this.submitButton.setAttribute(ATTRIBUTES.loading, '')
        this.getSpinner().classList.remove('hidden')

        const formData = new FormData(this.form)
        let formVariantId = formData.get('items[0][id]')
        if (!formVariantId) throw Error('No variant id found')
        const addedVariantId = parseInt(formVariantId.toString())

        function getShoeValue(index: number, className: string, context: HTMLElement | Document = document) {
            const personalizationValues: string[] = []
            const inputWrapper = context.querySelectorAll('.product-personalization__input-wrapper')[index] as HTMLElement
            if(!inputWrapper) return ''
            q.rl('.product-personalization__input-content', inputWrapper).forEach(inputContent => {
                const input = q.rs('input', inputContent) as HTMLInputElement
                if(inputContent.dataset.icon) personalizationValues.push(inputContent.dataset.icon as string)
                else if(input.value) personalizationValues.push(input.value)
            })
            const colorInput = q.rs(className, context) as HTMLInputElement
            personalizationValues.push(colorInput.value)

            return personalizationValues.join(', ')
        }

		// Default side (messaging: Right shoe)
        const leftValue = getShoeValue(0, '[name="left-color"]:checked', this)
		formData.set('items[1][properties[Right shoe]]', leftValue)

        // Preview metadata for cart rendering (product background and monogram text/color)
        const productHex = this.dataset.productColor || ''
        if (productHex) formData.set('items[1][properties[_product_color_hex]]', productHex)
        const leftColorInput = this.querySelector('[name="left-color"]:checked') as HTMLInputElement | null
        const leftHex = leftColorInput?.dataset.hex || ''
        if (leftHex) formData.set('items[1][properties[_monogram_hex]]', leftHex)
        // Build monogram text from left inputs (characters only, ignore icons)
        const leftInputsWrapper = this.querySelectorAll('.product-personalization__item[data-option="left"] .product-personalization__input-content')
        const leftMonogramChars: string[] = []
        leftInputsWrapper.forEach((content) => {
            const input = q.os<HTMLInputElement>('input', content as HTMLElement)
            if (input && !((content as HTMLElement).dataset.icon)) {
                const v = input.value?.trim()
                if (v) leftMonogramChars.push(v)
            }
        })
        if (leftMonogramChars.length) {
            formData.set('items[1][properties[_monogram_text]]', leftMonogramChars.join(''))
        }

        // Capture ordered preview sequence (icons and characters) from left inputs
        ;(() => {
            const tokens: string[] = []
            const iconUrls: string[] = []
            Array.from(leftInputsWrapper).forEach((contentEl) => {
                const content = contentEl as HTMLElement
                const iconHandle = content.dataset.icon
                const input = q.os<HTMLInputElement>('input', content)
                if (iconHandle) {
					const url = content.dataset.iconUrl || ''
                    if (url) {
                        tokens.push(`icon:${url}`)
                        iconUrls.push(url)
                    }
                } else if (input && input.value.trim()) {
                    tokens.push(`char:${input.value.trim()}`)
                }
            })
            if (tokens.length) {
                formData.set('items[1][properties[_monogram_preview]]', tokens.join('|'))
            }
            if (iconUrls.length) {
                formData.set('items[1][properties[_monogram_icon_urls]]', iconUrls.join('|'))
            }
        })()

		// Add-on side (messaging: Left shoe) - only if color selected
        if (this.querySelector('[name="right-color"]:checked')) {
            const rightValue = getShoeValue(1, '[name="right-color"]:checked', this)
			formData.set('items[1][properties[Left shoe]]', rightValue)
        }

        type AddToCartItem = {
            id: number
            quantity: number
            selling_plan?: number
            properties: Record<string, string>
            parent_id?: number
        }

        const items = createATCitems(formData) as AddToCartItem[]

        addItemsToCart(items, getDOMCartSectionApiIds())
            .then((cart) => {
                if ('status' in cart) {
                    return
                }

                publish(PUB_SUB_EVENTS.cartUpdate, {
                    source: 'product-form',
                    productVariantId: addedVariantId,
                })
                renderResponseToCartDrawer(cart)
            })
            .catch((e) => {
                console.error(e)
            })
            .finally(() => {
                this.submitButton.removeAttribute(ATTRIBUTES.loading)

                if (!this.error)
                    this.submitButton.removeAttribute('aria-disabled')
                this.getSpinner().classList.add('hidden')
                this.reset()
                this.classList.remove('active')
            })
    }
}